import { useState } from 'react';
import { FormField, Select } from '@/shared/components';
import { propertyTypeOptions, type PropertyType } from '@/shared/api';
import { SLEEPING_SPACE_LABELS, SleepingSpacesHint } from '../SleepingSpacesHint';
import { slugify, type WizardForm } from './wizardModel';
import { WizardField } from './WizardField';

interface StepApartmentProps {
  form: WizardForm;
  patch: (partial: Partial<WizardForm>) => void;
  errors: Record<string, string>;
}

export function StepApartment({ form, patch, errors }: StepApartmentProps) {
  const [slugTouched, setSlugTouched] = useState(false);

  return (
    <div className="space-y-4">
      {form.locationMode === 'existing' && form.existingObjectLabel && (
        <p className="border-line bg-card-alt text-ink-soft rounded-md border p-3 text-sm">
          Standort: <span className="text-ink font-medium">{form.existingObjectLabel}</span>
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <WizardField
          label="Name"
          value={form.name}
          error={errors.name}
          required
          className="md:col-span-2"
          onChange={(name) => patch(slugTouched ? { name } : { name, slug: slugify(name) })}
        />
        <WizardField
          label="Slug"
          value={form.slug}
          error={errors.slug}
          required
          hint="Kleinbuchstaben, Ziffern, Bindestriche."
          onChange={(slug) => {
            setSlugTouched(true);
            patch({ slug });
          }}
        />
        <FormField label="Typ" error={errors.propertyType} required>
          <Select
            value={form.propertyType}
            onChange={(e) => patch({ propertyType: e.target.value as PropertyType })}
          >
            {propertyTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
        {/* Ein bestehendes Objekt bringt seine Zeitzone mit; nur ein neuer Standort braucht sie. */}
        {form.locationMode === 'new' && (
          <WizardField
            label="Zeitzone"
            value={form.timezone}
            error={errors.timezone}
            required
            hint="Gilt für das ganze neue Objekt. Ohne Änderung gilt die des Workspace."
            onChange={(timezone) => patch({ timezone })}
          />
        )}
        <WizardField
          label={SLEEPING_SPACE_LABELS.adults}
          type="number"
          min={1}
          hint={SLEEPING_SPACE_LABELS.adultsHint}
          value={form.occupancyAdults}
          error={errors.occupancyAdults}
          onChange={(v) => patch({ occupancyAdults: Number(v) })}
        />
        <WizardField
          label={SLEEPING_SPACE_LABELS.children}
          type="number"
          min={0}
          hint={SLEEPING_SPACE_LABELS.childrenHint}
          value={form.occupancyChildren}
          error={errors.occupancyChildren}
          onChange={(v) => patch({ occupancyChildren: Number(v) })}
        />
        <div className="md:col-span-2">
          <SleepingSpacesHint adults={form.occupancyAdults} childSpaces={form.occupancyChildren} />
        </div>
        <WizardField
          label="Schlafzimmer"
          type="number"
          min={0}
          value={form.bedrooms}
          error={errors.bedrooms}
          onChange={(v) => patch({ bedrooms: Number(v) })}
        />
        <WizardField
          label="Bäder"
          type="number"
          min={0}
          value={form.bathrooms}
          error={errors.bathrooms}
          onChange={(v) => patch({ bathrooms: Number(v) })}
        />
      </div>
    </div>
  );
}
